import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCheckCircle, FiAlertTriangle, FiInfo, FiBarChart2, FiFileText } = FiIcons;

const AnalysisRunSummary = ({ questions, analysisTypes }) => {
  // Generate summary stats
  const subjectDistribution = {};
  const typeDistribution = {};
  const difficultyDistribution = {};
  
  questions.forEach(question => {
    // Count subjects
    if (question.subject) {
      subjectDistribution[question.subject] = (subjectDistribution[question.subject] || 0) + 1;
    }
    
    // Count question types
    if (question.type) {
      typeDistribution[question.type] = (typeDistribution[question.type] || 0) + 1;
    }
    
    // Count difficulty levels
    if (question.difficulty) {
      difficultyDistribution[question.difficulty] = (difficultyDistribution[question.difficulty] || 0) + 1;
    }
  });
  
  // Sort by frequency
  const sortedSubjects = Object.entries(subjectDistribution)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  const sortedTypes = Object.entries(typeDistribution)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white p-6 rounded-xl shadow-md border border-gray-100 mb-8 print:mb-12"
    >
      <div className="flex items-center space-x-3 mb-4">
        <div className="p-2 bg-primary-100 rounded-xl">
          <SafeIcon icon={FiFileText} className="text-primary-600 text-xl" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">Analysis Summary</h2>
          <p className="text-gray-600">Overview of your selected questions and analysis methods</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Questions summary */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">Questions Analyzed</h3>
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <SafeIcon icon={FiCheckCircle} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-900">{questions.length}</p>
              <p className="text-xs text-blue-700">Total questions</p>
            </div>
          </div>
          
          {/* Distribution by difficulty */}
          {Object.keys(difficultyDistribution).length > 0 && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs font-semibold text-blue-800 mb-1">By Difficulty</p>
              <div className="space-y-1">
                {Object.entries(difficultyDistribution).map(([difficulty, count]) => (
                  <div key={difficulty} className="flex justify-between items-center">
                    <span className="text-xs text-blue-700">{difficulty}</span>
                    <div className="flex items-center">
                      <span className="text-xs font-medium text-blue-900">{count}</span>
                      <span className="text-xs text-blue-700 ml-1">
                        ({Math.round((count / questions.length) * 100)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Subjects summary */}
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
          <h3 className="text-sm font-semibold text-purple-800 mb-2">Subjects Covered</h3>
          <div className="space-y-2">
            {sortedSubjects.length > 0 ? (
              sortedSubjects.map(([subject, count]) => (
                <div key={subject} className="flex justify-between items-center">
                  <span className="text-sm text-purple-900 truncate max-w-[70%]">{subject}</span>
                  <span className="text-xs font-medium px-2 py-1 bg-purple-100 rounded-full text-purple-800">
                    {count} questions
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-purple-700">No subject metadata available</p>
            )}
          </div>
          {Object.keys(subjectDistribution).length > 5 && (
            <p className="text-xs text-purple-600 mt-2 italic">
              +{Object.keys(subjectDistribution).length - 5} more subjects
            </p>
          )}
        </div>
        
        {/* Question types summary */}
        <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
          <h3 className="text-sm font-semibold text-amber-800 mb-2">Question Types</h3>
          <div className="space-y-2">
            {sortedTypes.length > 0 ? (
              sortedTypes.map(([type, count]) => (
                <div key={type} className="flex justify-between items-center">
                  <span className="text-sm text-amber-900 truncate max-w-[70%]">{type}</span>
                  <span className="text-xs font-medium px-2 py-1 bg-amber-100 rounded-full text-amber-800">
                    {count} questions
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-amber-700">No question type metadata available</p>
            )}
          </div>
          {Object.keys(typeDistribution).length > 5 && (
            <p className="text-xs text-amber-600 mt-2 italic">
              +{Object.keys(typeDistribution).length - 5} more types
            </p>
          )}
        </div>
      </div>
      
      {/* Analysis methods */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Analysis Methods Applied</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {analysisTypes.map((analysis) => (
            <div
              key={analysis.id}
              className="bg-white p-3 rounded-lg border border-gray-200 text-center"
            >
              <SafeIcon icon={analysis.icon} className="mx-auto text-primary-600 mb-2" />
              <p className="text-xs text-gray-800">{analysis.name}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Analysis tips */}
      <div className="mt-6 flex items-start space-x-3 bg-green-50 p-4 rounded-lg border border-green-100">
        <SafeIcon icon={FiInfo} className="text-green-600 mt-0.5 flex-shrink-0" />
        <div>
          <h4 className="text-sm font-medium text-green-800">Analysis Tips</h4>
          <p className="text-xs text-green-700 mt-1">
            For the most accurate analysis results, ensure your questions have complete metadata 
            including subject, type, and difficulty level. You can edit questions in the Question Bank 
            to add missing information.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default AnalysisRunSummary;